import React, { useMemo, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { FileUpload } from "primereact/fileupload";
import { Inplace, InplaceContent, InplaceDisplay } from "primereact/inplace";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { Password } from "primereact/password";
import { Stepper } from "primereact/stepper";
import { StepperPanel } from "primereact/stepperpanel";
import { calculateAge } from "./utils/calculateAge";
import "./Charon.css";

const steps = [
  "Credentials",
  "CSV upload",
  "Identities",
];

const Charon = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [credentials, setCredentials] = useState({ email: "", password: "" });
  const [loginState, setLoginState] = useState({ loading: false, error: "", success: "" });
  const [uploadState, setUploadState] = useState({ loading: false, error: "", success: "" });
  const [csvFile, setCsvFile] = useState(null);
  const [pilgrims, setPilgrims] = useState([]);
  const [identitiesCreated, setIdentitiesCreated] = useState(false);
  const [identityMessage, setIdentityMessage] = useState("");
  const [voucherCode, setVoucherCode] = useState("");
  const voucherCodeRef = useRef("");
  const showVoucherInput = false;

  const isCredentialsValid = useMemo(() => {
    return credentials.email.trim().length > 0 && credentials.password.trim().length > 0;
  }, [credentials]);

  const canCreateIdentities = Boolean(csvFile);
  const hasVoucherCode = voucherCode.trim().length > 0;
  const hasWaitlistValue = (pilgrim) => Number(pilgrim.WaitList>0) !== 0;
  const hasCredentialIdentity = (pilgrim) => {
    const credentialIdentityId = pilgrim?.credentialIdentityId;
    return Object.prototype.hasOwnProperty.call(pilgrim ?? {}, "credentialIdentityId") && credentialIdentityId != null;
  };
  const canCreateIdentityForPilgrim = (pilgrim) => !hasWaitlistValue(pilgrim) && !hasCredentialIdentity(pilgrim);
  const canRegisterPilgrim = (pilgrim) => !hasWaitlistValue(pilgrim) && hasCredentialIdentity(pilgrim) && pilgrim?.addedToCart !== true;
  const moveToStep = (nextStep) => {
    setActiveStep(nextStep);
  };

  const handleLogin = async () => {
    if (!isCredentialsValid || loginState.loading) {
      return;
    }

    setLoginState({ loading: true, error: "", success: "" });

    try {
      const payload = new URLSearchParams({
        login: credentials.email,
        password: credentials.password,
      });

      const response = await fetch("../backend/charon/login.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        credentials: "include",
        body: payload.toString(),
      });

      const data = await response.json();

      if (data.status === "ok") {
        setLoginState({
          loading: false,
          error: "",
          success: data.message || "Login succeeded.",
        });
        return;
      }

      setLoginState({
        loading: false,
        error: data.message || "Login failed.",
        success: "",
      });
    } catch (error) {
      console.error("Charon login failed", error);
      setLoginState({
        loading: false,
        error: "Login failed.",
        success: "",
      });
    }
  };

  const handleFileSelect = (event) => {
    const [selectedFile] = event.files || [];
    if (!selectedFile || uploadState.loading) {
      return;
    }

    setCsvFile(selectedFile);
    setPilgrims([]);
    setIdentitiesCreated(false);
    setIdentityMessage("");
    setUploadState({ loading: true, error: "", success: "" });

    const formData = new FormData();
    formData.append("pilgrims_csv", selectedFile);

    fetch("../backend/charon/loader.php", {
      method: "POST",
      credentials: "include",
      body: formData,
    })
      .then(async (response) => {
        const data = await response.json();

        if (data.status === "ok") {
          setPilgrims(Array.isArray(data.data) ? data.data : []);
          setUploadState({
            loading: false,
            error: "",
            success: data.message || "CSV uploaded successfully.",
          });
          return;
        }

        setUploadState({
          loading: false,
          error: data.message || "CSV upload failed.",
          success: "",
        });
        setPilgrims([]);
      })
      .catch((error) => {
        console.error("Charon CSV upload failed", error);
        setPilgrims([]);
        setUploadState({
          loading: false,
          error: "CSV upload failed.",
          success: "",
        });
      });
  };

  const handleFileClear = () => {
    setCsvFile(null);
    setPilgrims([]);
    setIdentitiesCreated(false);
    setIdentityMessage("");
    setUploadState({ loading: false, error: "", success: "" });
  };

  const handleCreateIdentities = () => {
    if (!csvFile) {
      return;
    }

    setIdentitiesCreated(true);
    setIdentityMessage(`Frontend placeholder: identities prepared from ${csvFile.name}.`);
  };

  const handleShowPilgrimData = (pilgrim) => {
    if (!pilgrim) {
      return;
    }
    const genderMap = {
      Male: "1",
      Female: "2",
      M: "1",
      F: "2",
    };
    const payload = new URLSearchParams({
      gender: genderMap[pilgrim.Gender] || pilgrim.Gender || "",
      firstname: pilgrim.Name || "",
      lastname: pilgrim.Surname || "",
      date_birth: pilgrim.BirthDate || "",
      email: pilgrim.Email || "",
      phone: pilgrim.Phone || "",
      address: pilgrim.Address || "",
    });

    fetch("../backend/charon/identities.php", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      credentials: "include",
      body: payload.toString(),
    })
      .then(async (response) => {
        const data = await response.json();
        setIdentityMessage(data.message || "Identity request sent.");
        if (data.status === "ok") {
          setPilgrims((currentPilgrims) =>
            currentPilgrims.map((currentPilgrim) =>
              currentPilgrim === pilgrim
                ? { ...currentPilgrim, credentialIdentityId: data?.data?.lastId ?? null }
                : currentPilgrim
            )
          );
          setIdentitiesCreated(true);
          return;
        }
        setPilgrims((currentPilgrims) =>
          currentPilgrims.map((currentPilgrim) =>
            currentPilgrim === pilgrim
              ? { ...currentPilgrim, credentialIdentityId: null }
              : currentPilgrim
          )
        );
      })
      .catch((error) => {
        console.error("Charon identity creation failed", error);
        setPilgrims((currentPilgrims) =>
          currentPilgrims.map((currentPilgrim) =>
            currentPilgrim === pilgrim
              ? { ...currentPilgrim, credentialIdentityId: null }
              : currentPilgrim
          )
        );
        setIdentityMessage("Identity request failed.");
      });
  };

  const handleShowCredentialIdentityId = (pilgrim) => {
    const credentialIdentityId = pilgrim?.credentialIdentityId;
    const payload = new URLSearchParams({
      vouchercode: voucherCodeRef.current || voucherCode || "",
      credentialIdentityId: credentialIdentityId || "",
    });

    fetch("../backend/charon/registration.php", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      credentials: "include",
      body: payload.toString(),
    })
      .then(async (response) => {
        const data = await response.json();
        if (data.status === "ok") {
          setPilgrims((currentPilgrims) =>
            currentPilgrims.map((currentPilgrim) =>
              currentPilgrim === pilgrim
                ? { ...currentPilgrim, addedToCart: true }
                : currentPilgrim
            )
          );
        }
        setIdentityMessage(data.message || "Registration request sent.");
      })
      .catch((error) => {
        console.error("Charon registration failed", error);
        setIdentityMessage("Registration request failed.");
      });
  };

  const handleCreateAllIdentities = () => {
    pilgrims
      .filter((pilgrim) => canCreateIdentityForPilgrim(pilgrim))
      .forEach((pilgrim) => {
        handleShowPilgrimData(pilgrim);
      });
  };

  const handleRegisterAllPilgrims = () => {
    pilgrims
      .filter((pilgrim) => canRegisterPilgrim(pilgrim))
      .forEach((pilgrim) => {
        handleShowCredentialIdentityId(pilgrim);
      });
  };

  return (
    <div className="charon-page">
      <div className="charon-shell">
        <section className="charon-hero">
          <p className="charon-eyebrow">Charon module</p>
          <h1>Import, create identities, and register pilgrims</h1>
          <p>
            This frontend-only flow is ready for the backend integration you mentioned.
            It guides the user through credentials, CSV intake, identity creation, and
            voucher-based registration in one PrimeReact stepper.
          </p>
        </section>

        <section className="charon-stepper">
          <Stepper activeStep={activeStep} linear={true}>
            <StepperPanel header={steps[0]}>
              <div className="charon-panel">
                <h2>Connect your account</h2>
                <p>Start by entering the email address and password that will be used for this import session.</p>

                <div className="charon-form-grid">
                  <div className="charon-field">
                    <label htmlFor="charon-email">Email address</label>
                    <InputText
                      id="charon-email"
                      type="email"
                      value={credentials.email}
                      onChange={(event) => {
                        setCredentials((current) => ({ ...current, email: event.target.value }));
                        setLoginState((current) => ({ ...current, error: "", success: "" }));
                      }}
                      placeholder="pilgrim.manager@example.org"
                    />
                  </div>

                  <div className="charon-field">
                    <label htmlFor="charon-password">Password</label>
                    <Password
                      id="charon-password"
                      value={credentials.password}
                      onChange={(event) => {
                        setCredentials((current) => ({ ...current, password: event.target.value }));
                        setLoginState((current) => ({ ...current, error: "", success: "" }));
                      }}
                      toggleMask
                      feedback={false}
                      placeholder="Enter your password"
                    />
                  </div>
                </div>

                {!isCredentialsValid ? (
                  <Message severity="info" text="Both fields are required before continuing." />
                ) : null}

                {loginState.error ? (
                  <Message severity="error" text={loginState.error} />
                ) : null}

                {loginState.success ? (
                  <Message severity="success" text={loginState.success} />
                ) : null}

                <div className="charon-actions">
                  <span />
                  <div className="charon-actions-right">
                    <Button
                      label={loginState.loading ? "Attempting login..." : "Attempt login"}
                      icon="pi pi-sign-in"
                      disabled={!isCredentialsValid || loginState.loading}
                      loading={loginState.loading}
                      onClick={handleLogin}
                    />
                    <Button
                      label="Next step"
                      icon="pi pi-arrow-right"
                      iconPos="right"
                      disabled={!loginState.success || loginState.loading}
                      onClick={() => moveToStep(1)}
                    />
                  </div>
                </div>
              </div>
            </StepperPanel>

            <StepperPanel header={steps[1]}>
              <div className="charon-panel">
                <h2>Upload the pilgrims CSV</h2>
                <p>Select one CSV file containing the pilgrims to import. As soon as the file is chosen, Charon sends it to the loader and reports whether the upload succeeded.</p>

                <FileUpload
                  mode="advanced"
                  name="pilgrimsCsv"
                  accept=".csv,text/csv"
                  multiple={false}
                  maxFileSize={5000000}
                  customUpload
                  auto={false}
                  chooseLabel="Choose CSV"
                  uploadLabel="Uploading..."
                  cancelLabel="Clear"
                  emptyTemplate={<p className="m-0">Drag and drop a CSV file here, or choose one manually.</p>}
                  uploadHandler={() => { }}
                  onSelect={handleFileSelect}
                  onClear={handleFileClear}
                  disabled={uploadState.loading}
                />

                <div className="charon-upload-note">
                  {csvFile ? (
                    <p><strong>Selected file:</strong> {csvFile.name}</p>
                  ) : (
                    <p>No CSV selected yet.</p>
                  )}
                </div>

                {uploadState.loading ? (
                  <Message severity="info" text="Uploading CSV..." />
                ) : null}

                {uploadState.error ? (
                  <Message severity="error" text={uploadState.error} />
                ) : null}

                {uploadState.success ? (
                  <Message severity="success" text={uploadState.success} />
                ) : null}

                <div className="charon-actions">
                  <Button
                    label="Back"
                    severity="secondary"
                    outlined
                    icon="pi pi-arrow-left"
                    onClick={() => moveToStep(0)}
                  />
                  <div className="charon-actions-right">
                    <Button
                      label="Continue to identity creation"
                      icon="pi pi-arrow-right"
                      iconPos="right"
                      disabled={!uploadState.success || uploadState.loading}
                      onClick={() => moveToStep(2)}
                    />
                  </div>
                </div>
              </div>
            </StepperPanel>

            <StepperPanel header={steps[2]}>
              <div className="charon-panel">
                <h2>Create identities</h2>
                <p>Once the CSV is present, the user can trigger identity creation for all pilgrims found in that file.</p>

                <Message
                  severity={canCreateIdentities ? "success" : "warn"}
                  text={
                    canCreateIdentities
                      ? `Ready to create identities from ${csvFile.name}.`
                      : "A CSV file is required before identities can be created."
                  }
                />

                {pilgrims.length ? (
                  <div className="charon-table-block">
                    <h3>Uploaded pilgrims</h3>
                    {showVoucherInput ? (
                      <Inplace closable className="charon-voucher-inplace">
                        <InplaceDisplay className="charon-voucher-display">
                          {voucherCode ? `Voucher code: ${voucherCode}` : "Enter a voucher code"}
                        </InplaceDisplay>
                        <InplaceContent className="charon-voucher-content">
                          <InputText
                            value={voucherCode}
                            onChange={(event) => {
                              voucherCodeRef.current = event.target.value;
                              setVoucherCode(event.target.value);
                            }}
                            placeholder="Enter a voucher code"
                          />
                        </InplaceContent>
                      </Inplace>
                    ) : <></>}
                    <div className="charon-table-actions">
                      <Button
                        label="Create all available identities (+)"
                        severity="secondary"
                        outlined
                        disabled={!pilgrims.some((pilgrim) => canCreateIdentityForPilgrim(pilgrim))}
                        onClick={handleCreateAllIdentities}
                      />
                      <Button
                        label="Register all created identites (V)"
                        severity="secondary"
                        outlined
                        disabled={!hasVoucherCode || !pilgrims.some((pilgrim) => canRegisterPilgrim(pilgrim))}
                        onClick={handleRegisterAllPilgrims}
                      />
                    </div>
                    <DataTable
                      value={pilgrims}
                      scrollable
                      scrollHeight="420px"
                      responsiveLayout="scroll"
                      size="small"
                      className="charon-table"
                    >
                      <Column field="Name" header="First name" sortable />
                      <Column field="Surname" header="Surname" sortable />
                      <Column
                        header="Age"
                        sortable
                        sortField="BirthDate"
                        body={(rowData) => (rowData.BirthDate ? calculateAge(rowData.BirthDate) : "")}
                      />
                      <Column
                        header="+"
                        body={(rowData) => {
                          const identityCreated = hasCredentialIdentity(rowData);
                          if (hasWaitlistValue(rowData)) {
                            return null;
                          }
                          return (
                            <Button
                              label={identityCreated ? "✓" : "+"}
                              severity={identityCreated ? "success" : "secondary"}
                              outlined
                              disabled={identityCreated}
                              onClick={() => handleShowPilgrimData(rowData)}
                            />
                          );
                        }}
                      />
                      {hasVoucherCode ? (
                        <Column
                          header="V"
                          body={(rowData) => {
                            const identityCreated = hasCredentialIdentity(rowData);
                            const addedToCart = rowData.addedToCart === true;
                            if (hasWaitlistValue(rowData)) {
                              return null;
                            }
                            return (
                              <Button
                                label="V"
                                severity={addedToCart ? "success" : "secondary"}
                                outlined
                                disabled={!identityCreated || addedToCart}
                                onClick={() => handleShowCredentialIdentityId(rowData)}
                              />
                            );
                          }}
                        />
                      ) : null}
                    </DataTable>
                  </div>
                ) : null}

                <div className="charon-actions">
                  <Button
                    label="Back"
                    severity="secondary"
                    outlined
                    icon="pi pi-arrow-left"
                    onClick={() => moveToStep(1)}
                  />
                  <div className="charon-actions-right">
                    <Button
                      label={identitiesCreated ? "Identities created" : "Create identities"}
                      icon="pi pi-users"
                      disabled={!canCreateIdentities}
                      onClick={handleCreateIdentities}
                    />
                  </div>
                </div>

                {identityMessage ? (
                  <div className="charon-summary-box">
                    <h3>Status</h3>
                    <p>{identityMessage}</p>
                  </div>
                ) : null}
              </div>
            </StepperPanel>
          </Stepper>
        </section>
      </div>
    </div>
  );
};

export default Charon;
